import { useQuery } from "@tanstack/react-query";
import { fetchMyPermissions } from "@/entities/session/api/sessionApi";
import { useAuthStore } from "@/shared/state/authstore";
import { PERMISSIONS_QUERY_KEY } from "@/entities/session/model/keys";

export function usePermissions() {
    const { accessToken, accountId } = useAuthStore();

    const { data: permissions, isLoading } = useQuery({
        queryKey: PERMISSIONS_QUERY_KEY,
        queryFn: fetchMyPermissions,
        enabled: !!accessToken && accountId !== null,
        // Permissions change for reasons the user did not cause (a grant made elsewhere, an account
        // going past due or being restored), and a stale answer is actively misleading: controls that
        // will fail when clicked, or a crippled UI after the firm has paid.
        //
        // What makes a reload correct is that this key is kept OUT of the persisted cache (see
        // UNPERSISTED_QUERY_KEY_PREFIXES) - the cache starts empty, so the list is always refetched.
        // staleTime therefore does not affect reload correctness at all, and a short window simply
        // stops every route navigation costing a request: ~78 components subscribe to this key, and
        // without it each new page's consumers refetch on mount. Within a session, a change still
        // surfaces on window focus, and the server enforces regardless of what the UI believes.
        staleTime: 15 * 1000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
    });

    const hasPermission = (permission: string) => {
        if (!permissions || !Array.isArray(permissions)) return false;
        return permissions.includes(permission);
    };

    return {
        permissions: permissions || [],
        isLoading,
        hasPermission
    };
}
